'use client';

import { useCallback, useState, useTransition } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  CheckCircleIcon,
  ChevronRightIcon,
  CircleAlertIcon,
  Paperclip,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import { Input } from '@kit/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@kit/ui/popover';
import { Textarea } from '@kit/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kit/ui/tooltip';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { SubmitFeedbackSchema } from '../lib/schema/submit-feedback.schema';
import { submitFeedbackAction } from '../lib/server/server-actions';

type FeedbackType = 'bug' | 'question' | 'feedback';

type FeedbackPopupContainerProps = React.PropsWithChildren<{
  metadata?: Record<string, unknown>;
}>;

const BUG_TYPE = 'bug';
const QUESTION_TYPE = 'question';
const FEEDBACK_TYPE = 'feedback';

export function FeedbackPopup({
  children,
  metadata = {},
}: FeedbackPopupContainerProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>

      <PopoverContent
        collisionPadding={50}
        className={'w-96'}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <FeedbackForm metadata={metadata} onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}

export function FeedbackForm(
  props: FeedbackPopupContainerProps & {
    onClose: () => void;
    hideEmail?: boolean;
    autoFocus?: boolean;
  },
) {
  const deviceInfo = useDeviceInfo();

  const [pending, startTransition] = useTransition();

  const [state, setState] = useState({
    success: false,
    error: false,
  });

  const form = useForm({
    resolver: zodResolver(SubmitFeedbackSchema),
    defaultValues: {
      device_info: deviceInfo,
      metadata: props.metadata,
      type: QUESTION_TYPE,
      email: '',
      text: '',
      attachment: undefined as
        | undefined
        | {
            name: string;
            type: string;
            image: string;
          },
    },
  });

  const type = form.watch('type');
  const displayEmail = type === QUESTION_TYPE && !props.hideEmail;
  const getPlaceholder = useGetPlaceholder();

  if (state.success) {
    return (
      <div
        className={
          'flex h-40 w-full flex-col space-y-2 px-4 py-2' +
          ' items-center justify-center'
        }
      >
        <div>
          <CheckCircleIcon className={'h-12 text-green-500'} />
        </div>

        <span className={'font-semibold'}>
          <Trans
            i18nKey={'feedback:successTitle'}
            defaults={'Thank you for your feedback!'}
          />
        </span>

        <Button variant={'outline'} onClick={() => props.onClose()}>
          <Trans i18nKey={'feedback:close'} defaults={'Close'} />
        </Button>
      </div>
    );
  }

  if (state.error) {
    return (
      <div
        className={
          'flex h-40 w-full flex-col space-y-2 px-4 py-2' +
          ' items-center justify-center'
        }
      >
        <div>
          <CircleAlertIcon className={'h-12 text-yellow-500'} />
        </div>

        <div className={'flex flex-col space-y-1'}>
          <span className={'font-semibold'}>
            <Trans
              i18nKey={'feedback:errorTitle'}
              defaults={'Sorry, Something went wrong!'}
            />
          </span>

          <span className={'text-muted-foregorund text-sm'}>
            <Trans
              i18nKey={'feedback:errorDescription'}
              defaults={'Please try again later or contact us directly.'}
            />
          </span>
        </div>

        <div className={'flex justify-start'}>
          <Button variant={'ghost'} onClick={props.onClose}>
            <Trans i18nKey={'feedback:close'} defaults={'Close'} />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => {
          startTransition(async () => {
            try {
              await submitFeedbackAction(data);
              setState({ success: true, error: false });
            } catch {
              setState({ success: false, error: true });
            }
          });
        })}
      >
        <div className={'flex flex-col space-y-4'}>
          <span className={'pr-8 font-semibold'}>
            <Trans
              i18nKey={'feedback:contactUs'}
              defaults={'Contact us about...'}
            />
          </span>

          <FeedbackTypeRadioGroup
            checked={type}
            setChecked={(type) => {
              form.setValue('type', type);
            }}
          />

          <If condition={displayEmail}>
            <FormField
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>
                      <Trans i18nKey={'feedback:email'} defaults={'Email'} />
                    </FormLabel>

                    <FormControl>
                      <Input
                        {...field}
                        required
                        type={'email'}
                        placeholder={''}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                );
              }}
              name={'email'}
            />
          </If>

          <FormField
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={getPlaceholder(type)}
                      name={'text'}
                      required
                      autoFocus={props.autoFocus}
                      // 16px on phones: iOS zooms into smaller fields
                      className={'h-24 resize-none text-base sm:text-sm'}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              );
            }}
            name={'text'}
          />

          <div className={'flex items-center justify-between'}>
            <AttachmentButton
              onChange={(attachment) => {
                form.setValue('attachment', {
                  name: attachment.file.name,
                  type: attachment.type,
                  image: attachment.image,
                });
              }}
            />

            <SubmitButtons onClose={props.onClose} pending={pending} />
          </div>
        </div>
      </form>
    </Form>
  );
}

function FeedbackTypeRadioGroup({
  checked,
  setChecked,
}: {
  checked: FeedbackType;
  setChecked: (value: FeedbackType) => void;
}) {
  return (
    <div className={'flex items-center space-x-1'}>
      <RadioItem
        checked={checked === FEEDBACK_TYPE}
        name={'type'}
        value={FEEDBACK_TYPE}
        className={
          'dark:hover:bg-background text-green-500 aria-checked:border-green-500 aria-checked:bg-green-50 dark:aria-checked:border-green-800 aria-checked:dark:bg-green-500/10'
        }
        onChange={setChecked}
      >
        <Trans i18nKey={'feedback:feedback'} defaults={'Feedback'} />
      </RadioItem>

      <RadioItem
        checked={checked === QUESTION_TYPE}
        name={'type'}
        value={QUESTION_TYPE}
        className={
          'dark:hover:bg-background text-blue-500 aria-checked:border-blue-500 aria-checked:bg-blue-50 dark:aria-checked:border-blue-800 aria-checked:dark:bg-blue-500/10'
        }
        onChange={setChecked}
      >
        <Trans i18nKey={'feedback:question'} defaults={'Question'} />
      </RadioItem>

      <RadioItem
        checked={checked === BUG_TYPE}
        name={'type'}
        value={BUG_TYPE}
        className={
          'dark:hover:bg-background text-red-500 aria-checked:border-red-500 aria-checked:bg-red-50 dark:aria-checked:border-red-800 aria-checked:dark:bg-red-500/10'
        }
        onChange={setChecked}
      >
        <Trans i18nKey={'feedback:bug'} defaults={'Bug'} />
      </RadioItem>
    </div>
  );
}

function RadioItem(
  props: React.PropsWithChildren<{
    name: string;
    value: FeedbackType;
    checked: boolean;
    className: string;
    onChange: (value: FeedbackType) => void;
  }>,
) {
  return (
    <label
      aria-checked={props.checked}
      className={cn(
        `relative flex w-full cursor-pointer items-center space-x-1.5 rounded-lg border border-transparent p-1.5 text-sm transition-colors`,
        {
          'dark:hover:bg-muted-foreground': !props.checked,
        },
        props.className,
      )}
    >
      <input
        checked={props.checked}
        name={props.name}
        type="radio"
        value={props.value}
        className={'hidden'}
        onChange={() => props.onChange(props.value)}
      />

      <div
        className={
          'flex h-4 w-4 items-center rounded-full bg-current' +
          ' justify-center'
        }
      >
        <span
          className={cn(
            'block h-3.5 w-3.5 rounded-full border-2 border-white',
            {
              'animate-in zoom-in-75 fade-in bg-current': props.checked,
              'bg-white': !props.checked,
            },
          )}
        />
      </div>

      <span className={'text-foreground text-center font-medium'}>
        {props.children}
      </span>
    </label>
  );
}

function SubmitButtons({
  onClose,
  pending,
}: {
  onClose: () => void;
  pending: boolean;
}) {
  return (
    <div className={'flex space-x-2'}>
      <Button
        type={'button'}
        disabled={pending}
        variant={'outline'}
        onClick={() => onClose()}
      >
        <Trans i18nKey={'feedback:close'} defaults={'Close'} />
      </Button>

      <Button disabled={pending}>
        {pending ? (
          <Trans i18nKey={'feedback:sending'} defaults={'Sending...'} />
        ) : (
          <span className={'flex items-center space-x-1'}>
            <span>
              <Trans i18nKey={'feedback:send'} defaults={'Send'} />
            </span>
            <ChevronRightIcon className={'h-4'} />
          </span>
        )}
      </Button>
    </div>
  );
}

function useDeviceInfo() {
  const { userAgent, language } = navigator;
  const width = window.innerWidth.toString();
  const height = window.innerHeight.toString();
  const screenName = document.title;
  const prefix = 'device_info';

  return {
    userAgent,
    language,
    width,
    height,
    screenName,
    prefix,
  };
}

function AttachmentButton(props: {
  onChange: (params: { file: File; image: string; type: string }) => void;
}) {
  const [attachment, setAttachment] = useState<{
    image: string;
    type: string;
    file: File;
  }>();

  const handleChange = (data: { file: File; image: string; type: string }) => {
    setAttachment(data);
    props.onChange(data);
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={'flex items-center space-x-2'}>
            <UploadImageButton onChange={handleChange} />

            <If condition={attachment}>
              {(attachment) => (
                <img
                  loading={'lazy'}
                  className={'object-cover'}
                  alt={'Preview'}
                  width={75}
                  height={75}
                  src={attachment.image}
                />
              )}
            </If>
          </div>
        </TooltipTrigger>

        <TooltipContent>
          <Trans
            i18nKey={'feedback:attachFileOrScreenshot'}
            defaults={'Attach file or screenshot'}
          />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function useGetPlaceholder() {
  const { t } = useTranslation('feedback');

  return useCallback(
    (checked: FeedbackType) => {
      switch (checked) {
        case FEEDBACK_TYPE:
          return t('feedbackPlaceholder');

        case QUESTION_TYPE:
          return t('questionPlaceholder');

        case BUG_TYPE:
          return t('bugPlaceholder');
      }
    },
    [t],
  );
}

function UploadImageButton(props: {
  onChange: (params: { file: File; image: string; type: string }) => void;
}) {
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();

    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const image = reader.result as string;
      const type = file.type;

      props.onChange({ image, file, type });
    };

    reader.readAsDataURL(file);
  };

  return (
    <Button type={'button'} size={'icon'} variant={'ghost'} asChild>
      <label className={'cursor-pointer'}>
        <Paperclip className={'h-4'} />
        <input
          type={'file'}
          className={'hidden'}
          name={'attachment'}
          accept="image/*"
          onChange={onChange}
        />
      </label>
    </Button>
  );
}
